import { useState } from "react";
import { ErrorCodes, ProblemError, hasGrant, usePrincipal } from "@csa/web-foundation";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Select,
  StatCard,
  StatusLabel,
  Table,
  TableCard,
  type StatusTone,
} from "../../ui";
import { useT } from "../../i18n/index";
import type { MessageKey } from "../../i18n/dictionaries";
import type {
  Campaign,
  CampaignChannel,
  CampaignStatus,
  LifecycleTrigger,
  ResolveResult,
  SuppressedReason,
} from "../../api/marketing-contracts";
import {
  useAttribution,
  useAudience,
  useCampaigns,
  useCreateCampaign,
  useLifecycleTriggers,
  useResolveCampaign,
  useUpdateCampaign,
  useSystemSegments,
} from "./use-marketing";
import { SavedSegments } from "./saved-segments";

// MK-FE — the Marketing console (marketing:read@all + marketing:manage@all). The
// no-spam ledger + attribution (read-only) and consent-bounded campaign definition
// / resolve. GÖNDERİM YOK: a prominent banner states send is deferred; resolve only
// evaluates the gate chain and writes targets. Built on the admin UI-kit (NO new
// palette). COMMISSION APPEARS NOWHERE.

const CHANNELS: CampaignChannel[] = ["EMAIL", "SMS", "PHONE", "PUSH"];
// A10 — the system-segment vocabulary is SERVED, not copied. See useSystemSegments.
// The previous local array duplicated apps/api/src/modules/crm/segments.ts and
// rendered raw kebab keys as the option text.

// GAP-AUDIT-3 D11 — the four governed suppression reasons, in the order the audience
// ledger already uses. The resolve tally returns all four and the surface used to add
// them up into a single "suppressed" figure, throwing away the ONLY thing that makes
// the number defensible: WHY each person was not reached. Same vocabulary as
// SUPPRESSION_GATES; consumed, never re-derived here.
const SUPPRESSION_REASONS: SuppressedReason[] = [
  "NO_CONSENT",
  "CARE_HOLD",
  "COOL_OFF",
  "TOUCH_CAP",
];
const SUPPRESSION_LABEL_KEY: Record<SuppressedReason, MessageKey> = {
  NO_CONSENT: "mk.audience.noConsent",
  CARE_HOLD: "mk.audience.careHold",
  COOL_OFF: "mk.audience.coolOff",
  TOUCH_CAP: "mk.audience.touchCap",
};

// GAP-AUDIT-2 B3 — bu yüzey nav'a `marketing:read` ile açılıyor ama yazmalar
// `marketing:manage` istiyor. Eskiden ne grant kontrolü ne de hata render'ı vardı:
// read-only bir kullanıcı üç butonu görüyor, tıklıyor, 403 alıyor ve ekranda HİÇBİR ŞEY
// olmuyordu — sessizce ölü kontrol. Artık yazma afordansları grant'a bağlı ve her
// mutation hatası sakin, tiplenmiş bir mesajla yüzeye çıkıyor.
function mutationErrorKey(err: unknown): MessageKey | null {
  if (!(err instanceof ProblemError)) return err ? "mk.error.detail" : null;
  if (err.status === 503) return "mk.error.unavailable";
  if (err.is(ErrorCodes.FORBIDDEN)) return "mk.error.noPermission";
  if (err.is(ErrorCodes.VALIDATION_FAILED)) return "mk.error.validation";
  return "mk.error.detail";
}

const CAMPAIGN_TONE: Record<CampaignStatus, StatusTone> = {
  DRAFT: "pending",
  ACTIVE: "active",
  CLOSED: "ended",
};
const CAMPAIGN_STATUS_LABEL: Record<CampaignStatus, MessageKey> = {
  DRAFT: "mk.campaign.status.draft",
  ACTIVE: "mk.campaign.status.active",
  CLOSED: "mk.campaign.status.closed",
};

export function MarketingConsole() {
  const { t } = useT();
  return (
    <div className="surf-stack" data-testid="marketing">
      <div className="surf-head">
        <div>
          <h1 className="surf-title">{t("mk.heading")}</h1>
          <p className="surf-sub muted">{t("mk.subtitle")}</p>
        </div>
      </div>

      {/* Send-deferred banner — the whole console dispatches nothing. */}
      <Card className="mk-banner" data-testid="mk-send-banner">
        <span className="mk-banner-icon" aria-hidden="true">
          <Icon name="lock" size={18} />
        </span>
        <span>{t("mk.sendDeferred")}</span>
      </Card>

      <IysStatusPanel />
      <AudienceSection />
      <LifecycleTriggersSection />
      <AttributionSection />
      <CampaignsSection statusLabel={CAMPAIGN_STATUS_LABEL} statusTone={CAMPAIGN_TONE} />
      <SavedSegments />
    </div>
  );
}

// ── Yaşam döngüsü tetikleyicileri (MK-3 — governed nudge cohorts, send OFF) ─────
const TRIGGER_LABEL: Record<LifecycleTrigger["trigger"], MessageKey> = {
  RENEWAL_DUE: "mk.trigger.renewalDue",
  COVERAGE_GAP: "mk.trigger.coverageGap",
};

function LifecycleTriggersSection() {
  const { t } = useT();
  const q = useLifecycleTriggers();

  return (
    <TableCard title={t("mk.trigger.title")} data-testid="mk-triggers">
      {q.isLoading && <LoadingState label={t("mk.loading")} />}
      {q.error != null && (
        <ErrorState
          title={t("mk.error.title")}
          description={
            q.error instanceof ProblemError ? q.error.problem.detail : t("mk.error.detail")
          }
        />
      )}
      {q.data && (
        <>
          <p className="muted mk-note">{t("mk.trigger.intro")}</p>
          {q.data.triggers.map((trg) => (
            <div key={trg.trigger} className="mk-trigger" data-testid={`mk-trigger-${trg.trigger}`}>
              <div className="mk-trigger-head">
                <span className="rep-card-title">{t(TRIGGER_LABEL[trg.trigger])}</span>
                <Badge variant="standard">
                  {trg.cohortHouseholds} {t("mk.trigger.households")}
                </Badge>
              </div>
              <Table>
                <thead>
                  <tr>
                    <th>{t("mk.audience.channel")}</th>
                    <th>{t("mk.audience.reachable")}</th>
                    <th>{t("mk.audience.noConsent")}</th>
                    <th>{t("mk.audience.careHold")}</th>
                    <th>{t("mk.audience.coolOff")}</th>
                    <th>{t("mk.audience.touchCap")}</th>
                  </tr>
                </thead>
                <tbody>
                  {trg.byChannel.map((c) => (
                    <tr key={c.channel} data-testid={`mk-trigger-${trg.trigger}-${c.channel}`}>
                      <td>{c.channel}</td>
                      <td>
                        <Badge variant="success">{c.reachable}</Badge>
                      </td>
                      <td>{c.suppressed.noConsent}</td>
                      <td>{c.suppressed.careHold}</td>
                      <td>{c.suppressed.coolOff}</td>
                      <td>{c.suppressed.touchCap}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ))}
          <p className="muted mk-note">{q.data.note}</p>
        </>
      )}
    </TableCard>
  );
}

// ── İYS durumu (design panel — static, honest go-live facts) ─────────────────
function IysStatusPanel() {
  const { t } = useT();
  const facts: { label: string; value: string }[] = [
    { label: t("mk.iys.registration"), value: t("mk.iys.registrationValue") },
    { label: t("mk.iys.send"), value: t("mk.iys.sendValue") },
    { label: t("mk.iys.residency"), value: t("mk.iys.residencyValue") },
  ];
  return (
    <Card className="mk-iys" data-testid="mk-iys">
      <span className="mk-iys-title">{t("mk.iys.title")}</span>
      <div className="mk-iys-facts">
        {facts.map((f) => (
          <div key={f.label} className="mk-iys-fact">
            <span className="muted">{f.label}</span>
            <strong>{f.value}</strong>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Ulaşılabilirlik defteri (audience) ───────────────────────────────────────
function AudienceSection() {
  const { t } = useT();
  const q = useAudience();

  return (
    <TableCard title={t("mk.audience.title")} data-testid="mk-audience">
      {q.isLoading && <LoadingState label={t("mk.loading")} />}
      {q.error != null && (
        <ErrorState
          title={t("mk.error.title")}
          description={
            q.error instanceof ProblemError ? q.error.problem.detail : t("mk.error.detail")
          }
        />
      )}
      {q.data && (
        <>
          <p className="muted mk-note">
            {t("mk.audience.gate")}: {q.data.gate.touchCapCount} / {q.data.gate.touchCapWindowDays}g
            · cool-off {q.data.gate.coolOffWindowDays}g
          </p>
          <Table data-testid="mk-audience-table">
            <thead>
              <tr>
                <th>{t("mk.audience.channel")}</th>
                <th>{t("mk.audience.reachable")}</th>
                <th>{t("mk.audience.noConsent")}</th>
                <th>{t("mk.audience.careHold")}</th>
                <th>{t("mk.audience.coolOff")}</th>
                <th>{t("mk.audience.touchCap")}</th>
                <th>{t("mk.audience.book")}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.byChannel.map((c) => (
                <tr key={c.channel} data-testid={`mk-audience-${c.channel}`}>
                  <td>{c.channel}</td>
                  <td>
                    <Badge variant="success">{c.reachable}</Badge>
                  </td>
                  <td>{c.suppressed.noConsent}</td>
                  <td>{c.suppressed.careHold}</td>
                  <td>{c.suppressed.coolOff}</td>
                  <td>{c.suppressed.touchCap}</td>
                  <td className="muted">{c.book}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="muted mk-note">{q.data.note}</p>
        </>
      )}
    </TableCard>
  );
}

// ── Kaynak → dönüşüm (attribution) ───────────────────────────────────────────
function AttributionSection() {
  const { t } = useT();
  const q = useAttribution();

  return (
    <TableCard title={t("mk.attr.title")} data-testid="mk-attribution">
      {q.isLoading && <LoadingState label={t("mk.loading")} />}
      {q.error != null && (
        <ErrorState
          title={t("mk.error.title")}
          description={
            q.error instanceof ProblemError ? q.error.problem.detail : t("mk.error.detail")
          }
        />
      )}
      {q.data && (
        <>
          <div className="mk-stat-row">
            <StatCard
              icon="conversation"
              iconTone="blue"
              value={q.data.totals.leads}
              label={t("mk.attr.leads")}
            />
            <StatCard
              icon="check-circle"
              iconTone="green"
              value={q.data.totals.conversions}
              label={t("mk.attr.conversions")}
            />
          </div>
          <Table data-testid="mk-attr-table">
            <thead>
              <tr>
                <th>{t("mk.attr.source")}</th>
                <th>{t("mk.attr.leads")}</th>
                <th>{t("mk.attr.conversions")}</th>
                <th>{t("mk.attr.rate")}</th>
                <th>{t("mk.attr.cac")}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.bySource.map((r) => (
                <tr key={r.source} data-testid={`mk-attr-${r.source}`}>
                  <td>{r.source}</td>
                  <td>{r.leads}</td>
                  <td>{r.conversions}</td>
                  <td>{(r.conversionRate * 100).toFixed(1)}%</td>
                  <td className="muted">{r.cac ?? t("mk.attr.external")}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="muted mk-note">{q.data.provenance}</p>
        </>
      )}
    </TableCard>
  );
}

// ── Kampanyalar (campaigns) ──────────────────────────────────────────────────
function CampaignsSection({
  statusLabel,
  statusTone,
}: {
  statusLabel: Record<CampaignStatus, MessageKey>;
  statusTone: Record<CampaignStatus, StatusTone>;
}) {
  const { t } = useT();
  const { me } = usePrincipal();
  const canManage = hasGrant(me, "marketing:manage", "all");
  const list = useCampaigns();
  const create = useCreateCampaign();
  const resolve = useResolveCampaign();
  const update = useUpdateCampaign();
  const writeError =
    mutationErrorKey(create.error) ??
    mutationErrorKey(resolve.error) ??
    mutationErrorKey(update.error);

  const [name, setName] = useState("");
  const [channel, setChannel] = useState<CampaignChannel>("EMAIL");
  // Governed segment vocabulary, from the server. Until it arrives the picker is
  // empty and Create is disabled — offering a guessed key would be the exact
  // drift this change removes.
  const segments = useSystemSegments();
  // Array.isArray, not `?? []`: a non-array body (an error shape, an empty
  // object) is truthy and would reach .map. Belt-and-braces on a read the
  // picker cannot render without.
  const segmentOptions = Array.isArray(segments.data) ? segments.data : [];
  const [segmentRef, setSegmentRef] = useState("");
  const effectiveSegment = segmentRef || segmentOptions[0]?.key || "";
  const [lastResolve, setLastResolve] = useState<Record<string, ResolveResult>>({});

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (!effectiveSegment) return;
    create.mutate(
      { name: trimmed, channel, segmentRef: effectiveSegment },
      { onSuccess: () => setName("") },
    );
  };

  return (
    <Card className="mk-card" data-testid="mk-campaigns">
      <div className="rep-card-head">
        <span className="rep-card-title">{t("mk.campaign.title")}</span>
      </div>

      {/* Yazma afordansları yalnız marketing:manage ile görünür — okuma yetkisi olan
          bir kullanıcıya tıklayınca 403 verecek buton göstermeyiz. */}
      {!canManage && (
        <p className="muted mk-note" data-testid="mk-readonly">
          {t("mk.readOnly")}
        </p>
      )}
      {writeError && (
        <p role="alert" className="form-error" data-testid="mk-write-error">
          {t(writeError)}
        </p>
      )}

      {/* Define a DRAFT campaign (governed system segment; saved:<id> also works). */}
      {canManage && (
        <div className="mk-campaign-form">
          <Input
            label={t("mk.campaign.name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            data-testid="mk-campaign-name"
          />
          <Select
            label={t("mk.campaign.channel")}
            value={channel}
            onChange={(e) => setChannel(e.target.value as CampaignChannel)}
            data-testid="mk-campaign-channel"
          >
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          <Select
            label={t("mk.campaign.segment")}
            value={effectiveSegment}
            onChange={(e) => setSegmentRef(e.target.value)}
            data-testid="mk-campaign-segment"
            disabled={segmentOptions.length === 0}
          >
            {segmentOptions.map((s) => (
              // The server's own Turkish label, not the kebab key.
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </Select>
          {/* WHY CREATE CAN BE UNCLICKABLE. If the segment read fails, the picker
              is empty AND disabled AND Create is disabled — three dead controls
              and no stated cause, which reads as "campaigns are broken" rather
              than "one read failed". */}
          {segments.error != null && <ErrorState title={t("mk.segmentsLoadFailed")} />}
          <Button
            variant="primary"
            onClick={submit}
            loading={create.isPending}
            disabled={!name.trim() || !effectiveSegment}
            leadingIcon={<Icon name="plus" size={16} />}
            data-testid="mk-campaign-create"
          >
            {t("mk.campaign.create")}
          </Button>
        </div>
      )}

      {list.isLoading && <LoadingState label={t("mk.loading")} />}
      {list.error != null && (
        <ErrorState
          title={t("mk.error.title")}
          description={
            list.error instanceof ProblemError ? list.error.problem.detail : t("mk.error.detail")
          }
        />
      )}
      {list.data && list.data.length === 0 && (
        <EmptyState
          icon="conversation"
          title={t("mk.campaign.empty.title")}
          description={t("mk.campaign.empty.desc")}
        />
      )}
      {list.data && list.data.length > 0 && (
        <Table data-testid="mk-campaign-table">
          <thead>
            <tr>
              <th>{t("mk.campaign.name")}</th>
              <th>{t("mk.campaign.channel")}</th>
              <th>{t("mk.campaign.segment")}</th>
              <th>{t("mk.campaign.statusCol")}</th>
              <th>{t("mk.campaign.action")}</th>
            </tr>
          </thead>
          <tbody>
            {list.data.map((c: Campaign) => (
              <tr key={c.id} data-testid={`mk-campaign-${c.id}`}>
                <td>{c.name}</td>
                <td>{c.channel}</td>
                <td className="muted">{c.segmentRef}</td>
                <td>
                  <StatusLabel tone={statusTone[c.status]}>{t(statusLabel[c.status])}</StatusLabel>
                </td>
                <td>
                  <div className="mk-campaign-actions">
                    {canManage && c.status !== "CLOSED" && (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={resolve.isPending && resolve.variables === c.id}
                        onClick={() =>
                          resolve.mutate(c.id, {
                            onSuccess: (data) =>
                              setLastResolve((prev) => ({ ...prev, [c.id]: data })),
                          })
                        }
                        data-testid={`mk-resolve-${c.id}`}
                      >
                        {t("mk.campaign.resolve")}
                      </Button>
                    )}
                    {canManage && c.status === "DRAFT" && (
                      <Button
                        variant="soft"
                        size="sm"
                        onClick={() => update.mutate({ id: c.id, status: "ACTIVE" })}
                        data-testid={`mk-activate-${c.id}`}
                      >
                        {t("mk.campaign.activate")}
                      </Button>
                    )}
                    {/* GAP-AUDIT-3 D11 — CLOSED existed as an enum value, a tone and a
                        label, and NO control could ever set it: an ACTIVE campaign had
                        no exit. A campaign that cannot be closed keeps appearing as
                        live work forever. */}
                    {canManage && c.status === "ACTIVE" && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => update.mutate({ id: c.id, status: "CLOSED" })}
                        data-testid={`mk-close-${c.id}`}
                      >
                        {t("mk.campaign.close")}
                      </Button>
                    )}
                    {/* D11 — the resolve tally arrives as a FOUR-WAY governed breakdown
                        {NO_CONSENT, CARE_HOLD, COOL_OFF, TOUCH_CAP} and was collapsed
                        into one "suppressed" number. That breakdown IS the compliance
                        evidence — it is the difference between "we did not reach 40
                        people" and "we did not reach 40 people because 12 are on a care
                        hold". Show it per reason. */}
                    {lastResolve[c.id] && (
                      <span className="mk-tally" data-testid={`mk-tally-${c.id}`}>
                        {lastResolve[c.id]!.tally.eligible} {t("mk.campaign.eligible")}
                        {SUPPRESSION_REASONS.filter((r) => lastResolve[c.id]!.tally[r] > 0).map(
                          (r) => (
                            <span key={r}>
                              {" · "}
                              {lastResolve[c.id]!.tally[r]} {t(SUPPRESSION_LABEL_KEY[r])}
                            </span>
                          ),
                        )}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
